import { uuid } from "@core/foundation/id";
import { omitUndefined } from "@core/foundation/object";
import { hasValue, isNullish } from "@core/foundation/types";
import type {
  JsonCreate,
  JsonPaginatedResult,
  JsonPaginationOptions,
  JsonPayload,
  JsonRepositoryHelperContract,
  JsonUpdate,
  JsonUpdateOptions,
  PK,
  SK,
} from "@core/ports/json-storage";
import {
  ETAG_FIELD,
  JsonDuplicateError,
  JsonWriteConflictError,
} from "@core/ports/json-storage";
import type { ZodObject, ZodType } from "@core/zod";
import { z } from "@core/zod";

import type { IndexedDbClient, IndexedDbOperation } from "./IndexedDbClient.ts";
import type { IndexedDbTransaction } from "./IndexedDbTransaction.ts";

interface Row {
  readonly PK: string;
  readonly SK: string;
  readonly SK2?: string;
  readonly data: unknown;
}

const StoredEtagSchema = z.object(
  { etag: z.string().optional() },
  { etag: "The etag storage stamped on the stored record" },
);

export class IndexedDbRepositoryHelper<
  Identifier extends ZodObject,
  Data extends ZodObject,
  EntityType extends string,
> implements
    JsonRepositoryHelperContract<
      Identifier,
      Data,
      IndexedDbTransaction,
      EntityType
    >
{
  public readonly __typeTag = "JsonRepositoryHelperContract";

  public readonly entityType: EntityType;

  private readonly indexedDb: IndexedDbClient;

  private readonly readSchema: ZodType<JsonPayload<Identifier, Data>>;

  private readonly tableName: string;

  public constructor({
    entityType,
    indexedDb,
    readSchema,
    tableName,
  }: {
    readonly entityType: EntityType;
    readonly indexedDb: IndexedDbClient;
    readonly readSchema: ZodType<JsonPayload<Identifier, Data>>;
    readonly tableName: string;
  }) {
    this.entityType = entityType;
    this.indexedDb = indexedDb;
    this.readSchema = readSchema;
    this.tableName = tableName;
  }

  /**
   * @throws {JsonDuplicateError} On duplicate.
   */
  public async create(
    create: JsonCreate<Identifier, Data, EntityType>,
  ): Promise<JsonPayload<Identifier, Data>> {
    const { operation, row } = this.buildAdd(create);

    const { committed } = await this.indexedDb.transact([operation]);

    if (!committed) {
      throw new JsonDuplicateError(`Duplicate PK=${create.PK} SK=${create.SK}`);
    }

    return this.convertRow(row);
  }

  public async getById(
    PK: PK<EntityType>,
    SK: SK,
  ): Promise<JsonPayload<Identifier, Data> | null> {
    const store = await this.getStore("readonly");
    const row: Row | undefined = await this.getRequestResult(
      store.get([PK, SK]),
    );

    if (isNullish(row)) {
      return null;
    }

    return this.convertRow(row);
  }

  public async getByIds(
    keys: readonly { readonly PK: PK<EntityType>; readonly SK: SK }[],
  ): Promise<readonly JsonPayload<Identifier, Data>[]> {
    if (keys.length === 0) {
      return [];
    }

    const store = await this.getStore("readonly");
    const rows = await Promise.all(
      keys.map(async key => {
        const row: Row | undefined = await this.getRequestResult(
          store.get([key.PK, key.SK]),
        );

        return row;
      }),
    );

    return this.convertRows(rows.filter(hasValue));
  }

  public async getByPK(
    PK: PK<EntityType>,
    options: JsonPaginationOptions = {},
  ): Promise<JsonPaginatedResult<Identifier, Data>> {
    return this.list({ PK, lower: [PK], options, upper: [PK, []] });
  }

  public async getBySKPrefix(
    PK: PK<EntityType>,
    SKPrefix: SK,
    options: JsonPaginationOptions = {},
  ): Promise<JsonPaginatedResult<Identifier, Data>> {
    const MAX_CODE_POINT = 0xff_ff;

    return this.list({
      PK,
      lower: [PK, SKPrefix],
      options,
      upper: [PK, `${SKPrefix}${String.fromCodePoint(MAX_CODE_POINT)}`],
    });
  }

  public prepareCreate(
    transaction: IndexedDbTransaction,
    create: JsonCreate<Identifier, Data, EntityType>,
  ): void {
    const { operation } = this.buildAdd(create);
    transaction.add(operation);
  }

  public prepareUpdateById(
    transaction: IndexedDbTransaction,
    PK: PK<EntityType>,
    SK: SK,
    data: JsonUpdate<Data>,
    options: JsonUpdateOptions = {},
  ): void {
    transaction.add(this.buildMerge(PK, SK, data, options));
  }

  /**
   * @throws {JsonWriteConflictError} If the stored etag differs from the
   *   one the options name.
   */
  public async updateById(
    PK: PK<EntityType>,
    SK: SK,
    data: JsonUpdate<Data>,
    options: JsonUpdateOptions = {},
  ): Promise<JsonPayload<Identifier, Data> | null> {
    const { committed } = await this.indexedDb.transact([
      this.buildMerge(PK, SK, data, options),
    ]);

    if (!committed) {
      return null;
    }

    return this.getById(PK, SK);
  }

  private buildAdd({
    PK,
    SK,
    SK2,
    payload,
  }: JsonCreate<Identifier, Data, EntityType>): {
    readonly operation: IndexedDbOperation;
    readonly row: Row;
  } {
    if (SK2 === "") {
      throw new Error(`An empty SK2 cannot key an index: PK=${PK} SK=${SK}`);
    }

    const data = { ...payload, [ETAG_FIELD]: uuid() } as const;
    const row: Row = hasValue(SK2) ? { PK, SK, SK2, data } : { PK, SK, data };

    return {
      operation: {
        run: (store, fail) => {
          const request = store.add(row);

          request.addEventListener("error", event => {
            if (request.error?.name === "ConstraintError") {
              event.preventDefault();
              fail();
            }
          });
        },
        storeName: this.tableName,
      },
      row,
    };
  }

  private buildMerge(
    PK: PK<EntityType>,
    SK: SK,
    data: JsonUpdate<Data>,
    { etag }: JsonUpdateOptions,
  ): IndexedDbOperation {
    const update = omitUndefined(data);

    return {
      run: (store, fail) => {
        const request: IDBRequest<Row | undefined> = store.get([PK, SK]);

        request.addEventListener("success", () => {
          const row = request.result;

          if (isNullish(row)) {
            fail();

            return;
          }

          const { etag: storedEtag } = StoredEtagSchema.parse(row.data);

          if (hasValue(etag) && storedEtag !== etag) {
            fail(new JsonWriteConflictError(`Etag conflict PK=${PK} SK=${SK}`));

            return;
          }

          store.put({
            ...row,
            data: { ...this.convertRow(row), ...update, [ETAG_FIELD]: uuid() },
          });
        });
      },
      storeName: this.tableName,
    };
  }

  private buildPaginatedResult(
    rows: readonly Row[],
    limit: number | undefined,
    cursorOf: (row: Row) => string | undefined,
  ): JsonPaginatedResult<Identifier, Data> {
    if (isNullish(limit)) {
      return { items: this.convertRows(rows) };
    }

    const excessRow = rows[limit];

    if (!excessRow) {
      return { items: this.convertRows(rows) };
    }

    return {
      items: this.convertRows(rows.slice(0, limit)),
      nextCursor: cursorOf(excessRow),
    };
  }

  private convertRow(row: Row): JsonPayload<Identifier, Data> {
    return this.readSchema.parse(row.data);
  }

  private convertRows(
    rows: readonly Row[],
  ): readonly JsonPayload<Identifier, Data>[] {
    return rows.map(row => this.convertRow(row));
  }

  private async getCursorRows({
    limit,
    order,
    range,
    source,
  }: {
    readonly limit: number | undefined;
    readonly order: "asc" | "desc";
    readonly range: IDBKeyRange;
    readonly source: IDBIndex | IDBObjectStore;
  }): Promise<readonly Row[]> {
    return new Promise((resolve, reject) => {
      // @mutable
      const rows: Row[] = [];

      const idbDirectionMap = { asc: "next", desc: "prev" } as const;
      const idbDirection = idbDirectionMap[order];
      const request = source.openCursor(range, idbDirection);

      request.addEventListener("success", () => {
        const cursor = request.result;

        if (!cursor) {
          resolve(rows);

          return;
        }

        const row: Row = cursor.value;
        rows.push(row);

        if (hasValue(limit) && rows.length >= limit + 1) {
          resolve(rows);

          return;
        }

        cursor.continue();
      });

      request.addEventListener("error", () => {
        reject(
          request.error ?? new Error("Failed to read an IndexedDB cursor."),
        );
      });
    });
  }

  private async getRequestResult<T>(request: IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      request.addEventListener("success", () => {
        resolve(request.result);
      });

      request.addEventListener("error", () => {
        reject(request.error ?? new Error("IndexedDB request failed."));
      });
    });
  }

  private async getStore(mode: IDBTransactionMode): Promise<IDBObjectStore> {
    const db = await this.indexedDb.connect();

    return db.transaction(this.tableName, mode).objectStore(this.tableName);
  }

  private async list({
    PK,
    lower,
    options: { cursor, index, limit, order = "asc" },
    upper,
  }: {
    readonly PK: PK<EntityType>;
    readonly lower: readonly unknown[];
    readonly options: JsonPaginationOptions;
    readonly upper: readonly unknown[];
  }): Promise<JsonPaginatedResult<Identifier, Data>> {
    let range: IDBKeyRange;

    if (isNullish(cursor)) {
      range = IDBKeyRange.bound(lower, upper);
    } else if (order === "asc") {
      range = IDBKeyRange.bound([PK, cursor], upper, true);
    } else {
      range = IDBKeyRange.bound(lower, [PK, cursor], false, true);
    }

    const store = await this.getStore("readonly");
    const source = hasValue(index) ? store.index(index) : store;
    const rows = await this.getCursorRows({ limit, order, range, source });

    return this.buildPaginatedResult(rows, limit, row => row[index ?? "SK"]);
  }
}
